import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { guardarConfirmado, reportarErrorGuardado, consultarEstadoIncierto } from "@/lib/guardado";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Field, TimeField } from "@/components/forms/fields";
import PhoneInput from "@/components/forms/PhoneInput";
import { Trash2, Plus } from "lucide-react";
const emptySite = {
  cliente_id: "", organization_id: "", nombre: "", direccion: "", ciudad: "", referencias: "", notas_acceso: "",
  modalidad: "residencial", responsable_operativo: "", telefono_central: "",
  telefono_central_pais: "MX", telefono_central_e164: null, responsable_sanitario: "", turnos: [],
  categorias_escalamiento: [], audio_habilitado: null, instruccion_por_paso: {},
};
const emptyCliente = {
  nombre_completo: "", telefono: "", telefono_pais: "MX", telefono_e164: null,
  correo: "", pais_residencia: "", notas: "",
};
const emptyTurno = { nombre: "", horario_inicio: "", horario_fin: "", responsable: "", rol: "", telefono: "", telefono_pais: "MX", telefono_e164: null };

export default function SiteForm({ open, onOpenChange, site, onSaved, organizationId, organizationNombre, organizationTipo }) {
  const [f, setF] = useState(emptySite);
  const [clientes, setClientes] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [nuevoCliente, setNuevoCliente] = useState(false);
  const [cf, setCf] = useState(emptyCliente);
  const [saving, setSaving] = useState(false);
  const [errorGuardado, setErrorGuardado] = useState(null);
  const enviandoRef = useRef(false);
  const cargadoRef = useRef(null);
  const modalidadTocadaRef = useRef(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setC = (k, v) => setCf((p) => ({ ...p, [k]: v }));
  const inst = f.modalidad === "institucional";
  const esNuevo = !site?.id;
  const orgFijada = !!organizationId;

  useEffect(() => {
    if (!open) { cargadoRef.current = null; return; }
    // Los turnos en captura no se reinician si el padre vuelve a consultar el sitio (DATA-008).
    const clave = site?.id || "nuevo";
    if (cargadoRef.current === clave) return;
    cargadoRef.current = clave;
    // UI-002: en un sitio NUEVO la modalidad arranca según el tipo AUTORITATIVO de la organización
    // (institucional/residencial). Si no hay señal (alta sin organización fijada) se mantiene el
    // valor anterior y se ajusta al elegir la organización, salvo que el usuario ya la haya tocado.
    modalidadTocadaRef.current = false;
    setF(site ? { ...emptySite, ...site, turnos: site.turnos || [] }
              : { ...emptySite, organization_id: organizationId || "",
                  modalidad: organizationTipo === "institucional" ? "institucional" : emptySite.modalidad });
    setNuevoCliente(false);
    setCf(emptyCliente);
    api.get("/clientes").then((r) => setClientes(r.data)).catch(() => {});
    if (!orgFijada) api.get("/organizations").then((r) => setOrgs(r.data)).catch(() => {});
  }, [open, site, organizationId, organizationTipo, orgFijada]);

  const elegirOrganizacion = (v) => setF((p) => {
    const tipo = orgs.find((o) => o.id === v)?.tipo;
    const ajusta = esNuevo && !modalidadTocadaRef.current && !!tipo;
    return { ...p, organization_id: v, modalidad: ajusta ? (tipo === "institucional" ? "institucional" : "residencial") : p.modalidad };
  });

  const setTurno = (i, obj) => setF((p) => {
    const t = [...(p.turnos || [])];
    t[i] = { ...t[i], ...obj };
    return { ...p, turnos: t };
  });
  const addTurno = () => setF((p) => ({ ...p, turnos: [...(p.turnos || []), { ...emptyTurno }] }));
  const delTurno = (i) => setF((p) => ({ ...p, turnos: (p.turnos || []).filter((_, x) => x !== i) }));

  const save = async () => {
    if (enviandoRef.current) return; // guard sincrónico: un doble clic no dispara dos escrituras
    enviandoRef.current = true;
    setSaving(true);
    setErrorGuardado(null);
    try {
      let clienteId = f.cliente_id;
      if (nuevoCliente && (cf.nombre_completo || cf.telefono || cf.correo)) {
        const rc = await api.post("/clientes", cf);
        clienteId = rc.data.id;
      }
      const payload = { ...f, cliente_id: clienteId || null,
                        organization_id: (organizationId || f.organization_id || null) };
      await guardarConfirmado({
        escribir: () => (site?.id ? api.put(`/sitios/${site.id}`, payload) : api.post("/sitios", payload)),
        refrescar: (res) => (onSaved ? onSaved(res.data) : undefined),
        exito: "Sitio guardado.",
      });
      onOpenChange(false);
    } catch (e) {
      // RBAC-012: el rechazo real (permiso/validación) queda visible y la captura se conserva.
      setErrorGuardado(reportarErrorGuardado(e, { consultarEstado: onSaved }));
    } finally {
      enviandoRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#012472]">{site?.id ? "Editar sitio" : "Nuevo sitio"}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Datos operativos del lugar donde Galarya presta el monitoreo.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {esNuevo && (
            <Field label="Organización (obligatoria · define el aislamiento de datos)">
              {orgFijada ? (
                <Input data-testid="site-organization-fija" className="rounded-xl bg-slate-50" readOnly
                  value={organizationNombre || "Organización seleccionada"} />
              ) : (
                <Select value={f.organization_id || ""} onValueChange={elegirOrganizacion}>
                  <SelectTrigger data-testid="site-organization-select" className="rounded-xl">
                    <SelectValue placeholder="Selecciona la organización" />
                  </SelectTrigger>
                  <SelectContent>
                    {orgs.filter((o) => o.estado !== "Inactivo").map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          )}
          {!esNuevo && site?.organization_id && (
            <p data-testid="site-organization-anclada" className="text-xs text-slate-500">
              Este sitio ya está anclado a una organización y ese anclaje no se puede mover.
            </p>
          )}
          <Field label="Modalidad del sitio">
            <Select value={f.modalidad} onValueChange={(v) => { modalidadTocadaRef.current = true; set("modalidad", v); }}>
              <SelectTrigger data-testid="site-modalidad-select" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="residencial">Residencial (casa/departamento)</SelectItem>
                <SelectItem value="institucional">Institucional (asilo/residencia/centro)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Nombre del sitio">
            <Input data-testid="site-nombre-input" className="rounded-xl" value={f.nombre} onChange={(e) => set("nombre", e.target.value)} />
          </Field>
          <Field label="Dirección completa">
            <Input className="rounded-xl" value={f.direccion} onChange={(e) => set("direccion", e.target.value)} />
          </Field>
          <Field label="Ciudad">
            <Input className="rounded-xl" value={f.ciudad} onChange={(e) => set("ciudad", e.target.value)} />
          </Field>

          {/* DATA-004: «Referencias para llegar» y «Notas de acceso» pertenecen al LUGAR, no a la
              modalidad: un centro también tiene indicaciones para llegar y notas de acceso. Antes
              se ocultaban al elegir Institucional (alta y edición) y el dato quedaba inalcanzable,
              aunque el modelo, el PUT y el Resumen del sitio ya los soportan sin distinción.
              Se reutilizan las MISMAS claves, persistencia y permisos. No son obligatorios y no se
              copia nada de la otra modalidad. Institucional NO es actor externo: esto no concede
              ningún acceso nuevo a los roles institucionales. */}
          <Field label="Referencias para llegar"
            hint={inst ? "Cómo llegar al centro (entrada, fachada, punto de referencia)." : undefined}>
            <Textarea data-testid="site-referencias-input" className="rounded-xl" value={f.referencias} onChange={(e) => set("referencias", e.target.value)} />
          </Field>
          <Field label="Notas de acceso"
            hint={inst ? "Cómo se accede al centro (recepción, control, restricciones por horario)." : undefined}>
            <Textarea data-testid="site-notas-acceso-input" className="rounded-xl" value={f.notas_acceso} onChange={(e) => set("notas_acceso", e.target.value)} />
          </Field>

          {inst && (
            <div className="rounded-xl border border-[#01CBE3]/40 bg-[#01CBE3]/5 p-4 space-y-4" data-testid="site-institucional-block">
              <p className="text-sm font-medium text-[#012472]">Datos del centro (institucional)</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Responsable operativo">
                  <Input data-testid="site-responsable-operativo" className="rounded-xl" value={f.responsable_operativo} onChange={(e) => set("responsable_operativo", e.target.value)} />
                </Field>
                <Field label="Responsable sanitario (opcional)">
                  <Input className="rounded-xl" value={f.responsable_sanitario} onChange={(e) => set("responsable_sanitario", e.target.value)} />
                </Field>
              </div>
              <Field label="Teléfono central del centro">
                <PhoneInput testId="site-central" value={{ telefono: f.telefono_central, telefono_pais: f.telefono_central_pais, telefono_e164: f.telefono_central_e164 }}
                  onChange={(t) => setF((p) => ({ ...p, telefono_central: t.telefono, telefono_central_pais: t.telefono_pais, telefono_central_e164: t.telefono_e164 }))} />
              </Field>
              {/* Protocolo institucional (P0) — configuración MÍNIMA. Si se deja vacía, la respuesta
                  institucional opera con el protocolo predeterminado (responsable de turno /
                  enfermería / supervisión). Son CATEGORÍAS OPERATIVAS: no son usuarios ni permisos. */}
              <Field label="Categorías de escalamiento interno (opcional · separadas por coma)"
                hint="Vacío = protocolo predeterminado: responsable de turno, enfermería, supervisión.">
                <Input data-testid="site-categorias-escalamiento" className="rounded-xl"
                  placeholder="responsable_turno, enfermeria, supervision"
                  value={(f.categorias_escalamiento || []).join(", ")}
                  onChange={(e) => set("categorias_escalamiento", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} />
              </Field>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input data-testid="site-audio-habilitado" type="checkbox" checked={f.audio_habilitado === true}
                  onChange={(e) => set("audio_habilitado", e.target.checked ? true : null)} />
                Comunicación por audio habilitada como APOYO (no sustituye la verificación en la ubicación)
              </label>
              <Field label="Instrucción del centro para la verificación en ubicación (opcional)">
                <Textarea data-testid="site-instruccion-verificacion" className="rounded-xl"
                  value={(f.instruccion_por_paso || {}).verificar_ubicacion || ""}
                  onChange={(e) => set("instruccion_por_paso", { ...(f.instruccion_por_paso || {}), verificar_ubicacion: e.target.value })} />
              </Field>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-[#012472]">Turnos (responsable por turno)</p>
                  <Button variant="ghost" size="sm" data-testid="site-add-turno" aria-label="Agregar un turno" className="text-[#01CBE3]" onClick={addTurno}><Plus className="h-4 w-4 mr-1" /> Agregar turno</Button>
                </div>
                {(f.turnos || []).length === 0 && <p className="text-xs text-amber-600">Sin turnos. El responsable de turno es clave durante una alarma.</p>}
                <div className="space-y-3">
                  {(f.turnos || []).map((t, i) => (
                    <div key={i} data-testid={`site-turno-${i}`} className="rounded-lg border border-gray-100 bg-white p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <Input data-testid={`site-turno-nombre-${i}`} aria-label={`Nombre del turno ${i + 1}`} className="rounded-lg text-sm w-40" placeholder="Nombre del turno" value={t.nombre || ""} onChange={(e) => setTurno(i, { nombre: e.target.value })} />
                        <Button variant="ghost" size="sm" className="text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FE6A55] focus-visible:ring-offset-1" data-testid={`site-del-turno-${i}`} aria-label={`Eliminar el turno ${t.nombre ? `“${t.nombre}”` : i + 1}`} onClick={() => delTurno(i)}><Trash2 className="h-4 w-4" /><span className="sr-only">Eliminar turno</span></Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <TimeField testId={`site-turno-inicio-${i}`} ariaLabel={`Hora de inicio del turno ${t.nombre || i + 1}`} className="rounded-lg text-sm" value={t.horario_inicio} onChange={(v) => setTurno(i, { horario_inicio: v })} />
                        <TimeField testId={`site-turno-fin-${i}`} ariaLabel={`Hora de fin del turno ${t.nombre || i + 1}`} className="rounded-lg text-sm" value={t.horario_fin} onChange={(v) => setTurno(i, { horario_fin: v })} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input className="rounded-lg text-sm" aria-label={`Responsable del turno ${t.nombre || i + 1}`} placeholder="Responsable" value={t.responsable || ""} onChange={(e) => setTurno(i, { responsable: e.target.value })} />
                        <Input className="rounded-lg text-sm" aria-label={`Rol del responsable del turno ${t.nombre || i + 1}`} placeholder="Rol" value={t.rol || ""} onChange={(e) => setTurno(i, { rol: e.target.value })} />
                      </div>
                      <PhoneInput testId={`site-turno-tel-${i}`} value={t} onChange={(x) => setTurno(i, x)} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-[#012472]">Cliente (quien contrata y paga)</p>
              <Button variant="ghost" size="sm" className="text-[#01CBE3] hover:text-[#01B5CB]" onClick={() => setNuevoCliente((v) => !v)}>
                {nuevoCliente ? "Elegir existente" : "＋ Nuevo cliente"}
              </Button>
            </div>
            {!nuevoCliente ? (
              <Select value={f.cliente_id || ""} onValueChange={(v) => set("cliente_id", v)}>
                <SelectTrigger data-testid="site-cliente-select" className="rounded-xl"><SelectValue placeholder="Selecciona un cliente" /></SelectTrigger>
                <SelectContent>
                  {clientes.map((c) => (<SelectItem key={c.id} value={c.id}>{c.nombre_completo || "(sin nombre)"}</SelectItem>))}
                </SelectContent>
              </Select>
            ) : (
              <div className="space-y-3 rounded-xl border border-gray-100 p-3">
                <Input className="rounded-xl" placeholder="Nombre completo" value={cf.nombre_completo} onChange={(e) => setC("nombre_completo", e.target.value)} />
                <PhoneInput testId="cliente-telefono" value={cf} onChange={(t) => setCf((p) => ({ ...p, ...t }))} />
                <Input className="rounded-xl" placeholder="Correo" value={cf.correo} onChange={(e) => setC("correo", e.target.value)} />
                <Input className="rounded-xl" placeholder="País de residencia" value={cf.pais_residencia} onChange={(e) => setC("pais_residencia", e.target.value)} />
                <Textarea className="rounded-xl" placeholder="Notas" value={cf.notas} onChange={(e) => setC("notas", e.target.value)} />
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="site-guardar-button" disabled={saving || (esNuevo && !(organizationId || f.organization_id))} aria-busy={saving} className={`rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white ${saving ? "pointer-events-none" : ""}`} onClick={save}>{saving ? "Guardando…" : "Guardar"}</Button>
          {errorGuardado && (
            <div role="alert" data-testid="site-error-banner" className="w-full rounded-xl border border-red-200 bg-red-50 p-3 text-left">
              <p className="text-sm font-semibold text-red-700">{errorGuardado.titulo}</p>
              <p className="text-xs text-red-600 mt-0.5">{errorGuardado.detalle}</p>
              <p className="text-xs text-red-500 mt-1">Tu captura sigue en pantalla; no se perdió nada.</p>
              {errorGuardado.incierto && (
                <Button variant="outline" size="sm" data-testid="site-consultar-estado" className="mt-2 rounded-lg"
                  onClick={() => consultarEstadoIncierto(() => onSaved && onSaved())}>Consultar estado</Button>
              )}
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
